import { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useParams,
  Navigate,
} from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { ToastProvider } from '@qh/shared';

// Layout
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';

// Modals
import { HiringIntakeForm } from './components/HiringIntakeForm';
import { ContactUsForm } from './components/ContactUsForm';

// Home pages
import { HomePage } from './components/HomePage';
import { LegacyHomePage } from './components/LegacyHomePage';

// Home sections (still used by /pricing and the legacy home page)
import { PricingSection } from './components/PricingSection';
import { FinalCTA } from './components/FinalCTA';
import { ROICalculator } from './components/ROICalculator';

// Static content pages
import { AboutPage } from './components/AboutPage';
import { CareersPage } from './components/CareersPage';
import { PrivacyPage } from './components/PrivacyPage';

// New Phase 6.0 pages
import EmployersPage from './pages/EmployersPage';
import { JobSeekersPage } from './pages/JobSeekersPage';
import { JobDetailPage } from './pages/JobDetailPage';
import { ProfilePage } from './pages/ProfilePage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { SentryTestPage } from './pages/SentryTestPage';
import { SignInPage } from './pages/SignInPage';
import { SignUpPage } from './pages/SignUpPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { InvitePage } from './pages/InvitePage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ResourceDetailPage, ResourcesPage } from './pages/ResourcesPage';
// import { StartupsPage } from './pages/StartupsPage';
import { ExFoundersPage } from './pages/ExFoundersPage';
import { CareerRestartPage } from './pages/CareerRestartPage';
import { ProductPage } from './pages/ProductPage';
import  EmployerBrandingPage  from './pages/EmployerBrandingPage';
import { ScreenersPage } from './pages/ScreenersPage';


// Phase 7-dashboard-b
import { RecoverAccountPage } from './pages/RecoverAccountPage';
import { DashboardLayout } from './pages/dashboard/DashboardLayout';
import { MyProfilePage } from './pages/dashboard/MyProfilePage';
import { MyResumesPage } from './pages/dashboard/MyResumesPage';
// Phase 7-dashboard-c
import { MyApplicationsPage } from './pages/dashboard/MyApplicationsPage';
import { MySavedJobsPage } from './pages/dashboard/MySavedJobsPage';
import { MyPreferencesPage } from './pages/dashboard/MyPreferencesPage';
import { AccountPage } from './pages/dashboard/AccountPage';
import { ProfileCompletionGate } from './components/profile/ProfileCompletionGate';
import { DeclarationGate } from './components/profile/DeclarationGate';
import { ProfileCompletionBanner } from './components/profile/ProfileCompletionBanner';

// Auth context
import { AuthProvider } from './context/AuthContext';
import { MasterDataProvider } from './context/MasterDataContext';
import { SavedJobsProvider } from './context/SavedJobsContext';
import { AppliedJobsProvider } from './context/AppliedJobsContext';

import { initAnalytics, trackPageView } from './lib/analytics';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

/**
 * Initializes GA4 once on mount (no-op when `VITE_GA_MEASUREMENT_ID` is unset)
 * and fires a `page_view` event on every React Router location change. The
 * setTimeout(0) lets `usePageTitle` hooks update `document.title` before the
 * tracker reads it — without it, GA4 receives the previous route's title.
 */
const AnalyticsTracker = () => {
  const location = useLocation();
  useEffect(() => {
    initAnalytics();
  }, []);
  useEffect(() => {
    const id = window.setTimeout(() => {
      trackPageView(location.pathname + location.search);
    }, 0);
    return () => window.clearTimeout(id);
  }, [location.pathname, location.search]);
  return null;
};

const PricingPage = ({ onHiringStart }: { onHiringStart: () => void }) => (
  <div className="min-h-screen pt-20">
    <PricingSection />
    <ROICalculator />
    <FinalCTA onHiringStart={onHiringStart} />
  </div>
);

const LegacyJobsRedirect = () => {
  const { slug } = useParams<{ slug: string }>();
  const { search, hash } = useLocation();
  const destination = slug
    ? `/job-seekers/${encodeURIComponent(slug)}`
    : '/job-seekers';

  return <Navigate to={`${destination}${search}${hash}`} replace />;
};

const NotFoundPage = () => (
  <main className="flex min-h-screen items-center justify-center bg-[#f5f6f2] px-6 text-center">
    <div>
      <p className="font-['JetBrains_Mono',monospace] text-sm font-medium tracking-[.08em] text-[#0b5c22]">
        404
      </p>
      <h1 className="mt-3 font-['Poppins',sans-serif] text-3xl font-semibold text-[#14150f]">
        Page not found
      </h1>
      <p className="mt-3 text-sm text-[#6b6f68]">The page you requested could not be found.</p>
    </div>
  </main>
);

function AppShell() {
  const [isHiringFormOpen, setIsHiringFormOpen] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);

  return (
    <>
      <ScrollToTop />
      <AnalyticsTracker />
      <Navbar />

      <AnimatePresence>
        {isHiringFormOpen && (
          <HiringIntakeForm isOpen={isHiringFormOpen} onClose={() => setIsHiringFormOpen(false)} />
        )}
        {isContactFormOpen && (
          <ContactUsForm isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />
        )}
      </AnimatePresence>

      {/* Phase 7-dashboard hotfix round 3 — banner is route-aware: it self-hides on
          surfaces where the prompt isn't relevant (home, marketing, auth pages). */}
      <ProfileCompletionBanner />

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/employers" element={<EmployersPage />} />
        <Route
          path="/legacy-home"
          element={<LegacyHomePage onHiringStart={() => setIsHiringFormOpen(true)} />}
        />
        <Route path="/job-seekers" element={<JobSeekersPage />} />
        <Route path="/job-seekers/:slug" element={<JobDetailPage />} />
        <Route path="/jobs" element={<LegacyJobsRedirect />} />
        <Route path="/jobs/:slug" element={<LegacyJobsRedirect />} />
        <Route path="/job" element={<NotFoundPage />} />
        <Route path="/job/*" element={<NotFoundPage />} />
        {/* Company pages were removed (clients are never identified publicly, recruitment workflow
            §6.2); old links and crawled URLs from the earlier sitemap land on the 404 page. */}
        <Route path="/companies" element={<NotFoundPage />} />
        <Route path="/companies/*" element={<NotFoundPage />} />
        <Route
          path="/pricing"
          element={<PricingPage onHiringStart={() => setIsHiringFormOpen(true)} />}
        />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/resources/:slug" element={<ResourceDetailPage />} />
        <Route path="/career-restart" element={<CareerRestartPage />} />
        <Route path="/product" element={<ProductPage />} />
        <Route path="/employer-branding" element={<EmployerBrandingPage />}/>
        <Route path="/screeners" element={<ScreenersPage />} />
        <Route path="/exfounders" element = {<ExFoundersPage/>}/>
        {/* <Route path="/startups" element = {<StartupsPage/>}/> */}
        

        {/* Auth */}
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="/recover-account" element={<RecoverAccountPage />} />
        {/* Recruitment workflow §6.3 — token-gated invite page (claim / sign in to apply). */}
        <Route path="/invite/:token" element={<InvitePage />} />

        {/* Phase 7-avatar (now redirects to /dashboard/profile) */}
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />

        {/* Phase 7-dashboard-b */}
        <Route path="/dashboard" element={<DashboardLayout />}>
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<MyProfilePage />} />
          <Route path="resumes" element={<MyResumesPage />} />
          <Route path="applications" element={<MyApplicationsPage />} />
          <Route path="saved-jobs" element={<MySavedJobsPage />} />
          <Route path="preferences" element={<MyPreferencesPage />} />
          <Route path="account" element={<AccountPage />} />
        </Route>

        {/* Sentry verification — wired only in dev to keep prod builds clean. */}
        {import.meta.env.VITE_SENTRY_ENVIRONMENT === 'development' && (
          <Route path="/sentry-test" element={<SentryTestPage />} />
        )}
      </Routes>

      <Footer onContactOpen={() => setIsContactFormOpen(true)} />
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <MasterDataProvider>
          <ToastProvider>
            <SavedJobsProvider>
              <AppliedJobsProvider>
                <ProfileCompletionGate>
                  {/* Recruitment workflow §6.4 — declaration popup on apply (step 5e). */}
                  <DeclarationGate>
                    <AppShell />
                  </DeclarationGate>
                </ProfileCompletionGate>
              </AppliedJobsProvider>
            </SavedJobsProvider>
          </ToastProvider>
        </MasterDataProvider>
      </AuthProvider>
    </Router>
  );
}
